import { TokenBalance } from '@/constants/types';
import { arbitrum, avalanche, base, bsc, mainnet, optimism, polygon, sepolia } from 'wagmi/chains';

// Helper function to get chain by ID
export const getChainById = (chainId: number) => {
    // Custom chain configurations with reliable RPC endpoints
    const customChains = {
        1: { // Ethereum Mainnet
            ...mainnet,
            rpcUrls: {
                default: { http: ['/api/rpc?chain=ethereum'] },
                public: { http: ['/api/rpc?chain=ethereum'] }
            }
        },
        137: { // Polygon Mainnet
            ...polygon,
            rpcUrls: {
                default: { http: ['/api/rpc?chain=polygon'] },
                public: { http: ['/api/rpc?chain=polygon'] }
            }
        },
        11155111: { // Sepolia Testnet
            ...sepolia,
            rpcUrls: {
                default: { http: ['https://sepolia.llamarpc.com'] },
                public: { http: ['https://sepolia.llamarpc.com'] }
            }
        },
        56: {
            ...bsc,
            rpcUrls: {
                default: { http: ['/api/rpc?chain=bsc'] },
                public: { http: ['/api/rpc?chain=bsc'] }
            }
        },
        8453: {
            ...base,
            rpcUrls: {
                default: { http: ['/api/rpc?chain=base'] },
                public: { http: ['/api/rpc?chain=base'] }
            }
        },
        42161: {
            ...arbitrum,
            rpcUrls: {
                default: { http: ['/api/rpc?chain=arbitrum'] },
                public: { http: ['/api/rpc?chain=arbitrum'] }
            }
        },
        10: { // Optimism
            ...optimism,
            rpcUrls: {
                default: { http: ['/api/rpc?chain=optimism'] },
                public: { http: ['/api/rpc?chain=optimism'] }
            }
        },
        43114: { // Avalanche
            ...avalanche,
            rpcUrls: {
                default: { http: ['/api/rpc?chain=avalanche'] },
                public: { http: ['/api/rpc?chain=avalanche'] }
            }
        },
        4663: { // Robinhood Chain (Arbitrum Orbit L2)
            id: 4663,
            name: 'Robinhood Chain',
            nativeCurrency: { name: 'Ethereum', symbol: 'ETH', decimals: 18 },
            rpcUrls: {
                default: { http: ['/api/rpc?chain=robinhood'] },
                public: { http: ['/api/rpc?chain=robinhood'] }
            },
            blockExplorers: {
                default: { name: 'Blockscout', url: 'https://robinhoodchain.blockscout.com' }
            }
        }
    };

    return customChains[chainId as keyof typeof customChains] || mainnet;
};

// Get the correct chain ID for a token based on its network
export const getChainIdForToken = (token: TokenBalance): number => {
    if (!token) return 1; // Default to Ethereum

    // Primary: use evmChainId extracted directly from Para's NetworkMetadata
    if (token.evmChainId) return token.evmChainId;

    // Fallback: map network names to chain IDs (include all known Para name variants)
    const networkToChainId: { [key: string]: number } = {
        'Ethereum': 1,
        'Polygon': 137,
        'Base': 8453,
        'Arbitrum': 42161,
        'Optimism': 10,
        'Avalanche': 43114,
        'Sepolia': 11155111,
        'Bsc': 56,
        'BNB': 56,
        'BNB Smart Chain': 56,
        'Robinhood': 4663,
        'Robinhood Chain': 4663,
    };

    return networkToChainId[token.network || 'Ethereum'] || 1;
};